import { FileOutlined, FullscreenOutlined } from '@ant-design/icons';
import { Button, Modal, Spin, Tooltip } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useFileAction } from '../../hooks/useFileAction';
import { useViewFile } from '../../hooks/useViewFile';
import { useViewInteractFile } from '../../hooks/useViewInteractFile';
import { theme } from '../../styles';
import DownloadFile from '../DownloadFile';
import {
  PREVIEWABLE_EXTENSIONS,
  DOCX_EXTENSIONS,
  formatFileSize,
  IFolderNode,
  IMAGE_EXTENSIONS,
  TEXT_EXTENSIONS,
} from './utils';
import { DocxPreview } from '../DocxPreview';
import { PdfPreview } from '../PdfPreview';
import type { ICommentBox, IPageHighlights } from '../PdfPreview';

interface IFilePreviewPanelProps {
  file: IFolderNode;
  modelInstanceId?: string;
  downloadButtonText?: string;
  previewButtonNotAvailableText?: string;
  shouldShowDownload?: boolean;
  shouldShowFullSize?: boolean;
  /** Hide the built-in header (name/size) + download/fullscreen chrome — for
   * hosts (e.g. a compare workspace) that already render their own tab/header
   * around this panel. Default true (unchanged behaviour for existing callers). */
  showHeader?: boolean;
  /** Passed straight through to the inner `PdfPreview` when the file is a PDF. */
  highlights?: Map<number, IPageHighlights>;
  activeChangeId?: string | null;
  onChangeSelect?: (changeId: string) => void;
  commentBoxes?: Map<number, ICommentBox[]>;
  activeCommentId?: string | null;
  onCommentSelect?: (id: string) => void;
  scale?: number;
  onScaleChange?: (scale: number) => void;
}

const SYNTHETIC_EVENT = {
  stopPropagation: () => {},
} as unknown as React.MouseEvent<HTMLButtonElement>;

const FilePreviewPanel: React.FC<IFilePreviewPanelProps> = ({
  file,
  modelInstanceId,
  downloadButtonText = 'Download',
  previewButtonNotAvailableText = 'Preview not available for this file type',
  shouldShowDownload = true,
  shouldShowFullSize = true,
  showHeader = true,
  highlights,
  activeChangeId,
  onChangeSelect,
  commentBoxes,
  activeCommentId,
  onCommentSelect,
  scale,
  onScaleChange,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const isPreviewable = PREVIEWABLE_EXTENSIONS.includes(
    file.extension?.toLowerCase() ?? ''
  );

  const isImage = IMAGE_EXTENSIONS.includes(
    file.extension?.toLowerCase() ?? ''
  );

  const isText = TEXT_EXTENSIONS.includes(file.extension?.toLowerCase() ?? '');

  const isDocx = DOCX_EXTENSIONS.includes(file.extension?.toLowerCase() ?? '');

  const { viewFile: viewCoreFile, isLoadingFile: isLoadingCoreFile } =
    useViewFile(url => setPreviewUrl(url));

  const { viewFile: viewInteractFile, isLoadingFile: isLoadingInteractFile } =
    useViewInteractFile(url => setPreviewUrl(url));

  const { handleClick, isLoading } = useFileAction({
    fileId: file.id,
    filePath: file.path,
    modelInstanceId,
    isLoadingCore: isLoadingCoreFile,
    isLoadingInteract: isLoadingInteractFile,
    onCoreAction: viewCoreFile,
    onInteractAction: viewInteractFile,
  });

  const handleClickRef = useRef(handleClick);
  handleClickRef.current = handleClick;

  useEffect(() => {
    setPreviewUrl(null);
    if (!isPreviewable || !file.id || !file.path) return;
    handleClickRef.current(SYNTHETIC_EVENT);
  }, [file?.id, file?.path, isPreviewable]);

  const renderBody = () => {
    if (isLoading) {
      return (
        <SpinWrapper>
          <Spin size="large" />
        </SpinWrapper>
      );
    }

    if (previewUrl && isImage) {
      return (
        <ImageWrapper>
          <img src={previewUrl} />
        </ImageWrapper>
      );
    }

    if (previewUrl && isText) {
      return <TextIframe src={previewUrl} title={file.name} />;
    }

    if (previewUrl && isDocx) {
      return (
        <DocxPreview
          url={previewUrl}
          fileName={file.name}
          scale={scale}
          onScaleChange={onScaleChange}
        />
      );
    }

    if (previewUrl && isPreviewable) {
      return (
        <PdfPreview
          url={previewUrl}
          highlights={highlights}
          activeChangeId={activeChangeId}
          onChangeSelect={onChangeSelect}
          commentBoxes={commentBoxes}
          activeCommentId={activeCommentId}
          onCommentSelect={onCommentSelect}
          scale={scale}
          onScaleChange={onScaleChange}
        />
      );
    }

    return (
      <Fallback>
        <FileOutlined style={{ fontSize: 64, color: theme.colors.primary }} />
        <p>{previewButtonNotAvailableText}</p>
      </Fallback>
    );
  };

  return (
    <PanelWrapper>
      {showHeader && (
        <PanelHeader>
          <FileInfo>
            <FileName>{file.name}</FileName>
            {file.size !== undefined && (
              <FileSize>{formatFileSize(file.size)}</FileSize>
            )}
          </FileInfo>
          <ActionButtons>
            {shouldShowFullSize && (
              <Tooltip title="Fullscreen">
                <Button
                  type="text"
                  icon={<FullscreenOutlined />}
                  onClick={() => setIsFullscreen(true)}
                />
              </Tooltip>
            )}
            {shouldShowDownload && (
              <DownloadFile
                fileId={file.id}
                filePath={file.path}
                modelInstanceId={modelInstanceId}
                label={downloadButtonText}
              />
            )}
          </ActionButtons>
        </PanelHeader>
      )}
      <PanelBody>{renderBody()}</PanelBody>
      <Modal
        open={isFullscreen}
        onCancel={() => setIsFullscreen(false)}
        title={file.name}
        footer={null}
        width="100vw" // Spans the full viewport width
        style={{
          maxWidth: '100vw',
          top: 0,
          paddingBottom: 0,
        }}
        styles={{
          content: {
            height: '100dvh', // Makes internal content 100% viewport height
            borderRadius: 0, // Removes default rounded corners for full screen look
            display: 'flex',
            flexDirection: 'column',
          },
          body: {
            flex: 1,
            overflowY: 'auto', // Ensures content scrolls if it overflows the screen
          },
        }}
      >
        {renderBody()}
      </Modal>
    </PanelWrapper>
  );
};

export default FilePreviewPanel;

const PanelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  flex-shrink: 0;
`;

const FileInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
  min-width: 0;
`;

const FileName = styled.span`
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textPrimary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FileSize = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const PanelBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  position: relative;
`;

const SpinWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const Fallback = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const TextIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

const ImageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  img {
    max-width: 100%;
  }
`;
